import * as React from "react";
import { cls } from "@utdanningsdirektoratet/utils/cls";
import styles from "./MenuPanel.module.less";
import { mainPanelId } from "../MainPanel/MainPanel";
import { useStore } from "../Page";
import { Banner } from "./Banner";

type MenuPanelProps = {
  title: string;
  menu?: Array<React.ReactNode>;
  external?: boolean;
  className?: string;
  children?: React.ReactNode;
  jumpToContent?: string;
  readOnlyAccess?: boolean;
};

export const menuPanelId = "MenuPanelId";

const MenuPanel: React.FC<MenuPanelProps> = ({
  children,
  title,
  menu,
  external,
  className,
  jumpToContent = "Hopp til innhold",
  readOnlyAccess = false,
}) => {
  const [, setState] = useStore();

  const [height, setHeight] = React.useState(external ? "0px" : "57px");
  const ref = React.useRef<HTMLElement>(null);

  React.useEffect(() => {
    function handleResize() {
      if (ref.current?.clientHeight) setHeight(`${ref.current.offsetHeight}px`);
    }

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const menuPanelClass = cls(
    {
      [styles.menuPanel]: true,
      [styles.menuPanelExternal]: external,
    },
    className
  );

  const listItemClassName = cls({
    [styles.menuPanelListItem]: true,
  });

  React.useLayoutEffect(() => {
    setState((prev) => ({ ...prev, menuPanel: true }));
    return () => {
      setState((prev) => ({ ...prev, menuPanel: false }));
    };
  }, [setState]);

  React.useEffect(() => {
    document.documentElement.style.setProperty("--menu-height", external ? "0px" : height);
    return () => {
      document.documentElement.style.setProperty("--menu-height", "0px");
    };
  }, [height, external]);

  return (
    <header ref={ref} id={menuPanelId} className={menuPanelClass}>
      <div className={styles.menuPanelPanel}>
        <a className={styles.menuPanelJumpToContent} href={`#${mainPanelId}`}>
          {jumpToContent}
        </a>
        <div className={styles.menuPanelTitle}>{title}</div>
        {menu && (
          <nav className={styles.menuPanelNav}>
            <ul className={styles.menuPanelList}>
              {menu.map((menuItem, i) => (
                <li key={i} className={listItemClassName}>
                  {menuItem}
                </li>
              ))}
            </ul>
          </nav>
        )}
        {children && (
          <div className={styles.menuPanelContent}>
            <div>{children}</div>
          </div>
        )}
      </div>
      {readOnlyAccess && <Banner />}
    </header>
  );
};

export default MenuPanel;
